import { defaults } from 'ol/control/defaults';
import { Tile as TileLayer } from 'ol/layer';
import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';
import { XYZ } from 'ol/source';
import View from 'ol/View';

import MapUtils from './MapUtils';

import { VWORLD_API_KEY, OL_MAX_ZOOM } from '@/config';
import { MAPS } from '@/define/olMaps';
import CoordUtils from '@/utils/CoordUtils';

/**
 * OpenLayers 맵 핵심 기능 관리
 */
class MapCore {
  constructor() {
    this.map = null;
    this.currentMapId = null;
  }

  /**
   * 맵 초기화
   * @param {string} target - 맵이 렌더링될 DOM 엘리먼트 ID
   * @param {Object} options - { center: {lat, lng}, zoom: number }
   * @param {string} defaultMapId - 기본 맵 ID
   * @returns {Map} OpenLayers 맵 인스턴스
   */
  init(target, options, defaultMapId) {
    this.currentMapId = defaultMapId;

    const view = new View({
      center: fromLonLat(CoordUtils.arrayFromObject(options.center)),
      zoom: options.zoom,
      maxZoom: OL_MAX_ZOOM,
      extent: MapUtils.getExtent(),
    });

    // 지도 선언
    this.map = new Map({
      target,
      view,
      layers: this.createLayers(this.currentMapId, 0),
      controls: defaults({ zoom: false, attribution: false, rotate: false }),
    });

    return this.map;
  }

  /**
   * 레이어 생성
   * @param {string} mapId - 맵 ID ('GOOGLE' 또는 'VWORLD')
   * @param {number} typeIndex - 맵 타입 인덱스
   * @returns {Array} 레이어 배열
   */
  createLayers(mapId, typeIndex) {
    const type = MAPS[mapId].types[typeIndex];

    // Google Map
    if (mapId === 'GOOGLE') {
      return [
        new TileLayer({
          source: new XYZ({ url: `https://mt0.google.com/vt/lyrs=${type.key}&hl=en&x={x}&y={y}&z={z}` }),
        }),
      ];
    }
    // VWorld Map
    else if (mapId === 'VWORLD') {
      const isSatellite = type.key === 'Satellite';
      const extension = isSatellite ? 'jpeg' : 'png';

      const layers = [
        new TileLayer({
          source: new XYZ({
            url: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_API_KEY}/${type.key}/{z}/{y}/{x}.${extension}`,
          }),
        }),
      ];

      // 위성사진인 경우 Hybrid 레이어 추가
      if (isSatellite) {
        layers.push(
          new TileLayer({
            source: new XYZ({ url: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_API_KEY}/Hybrid/{z}/{y}/{x}.png` }),
          })
        );
      }
      return layers;
    }
  }

  /**
   * 맵 변경
   * @param {string} mapId - 변경할 맵 ID
   * @param {number} typeIndex - 맵 타입 인덱스
   */
  changeMap(mapId, typeIndex) {
    // 변경된 지도 아이디 갱신
    this.currentMapId = mapId;

    const layers = this.createLayers(mapId, typeIndex);
    const vectorLayers = this.map
      .getLayers()
      .getArray()
      .filter((layer) => layer instanceof require('ol/layer').Vector);

    this.map.setLayers([...layers, ...vectorLayers]);

    const options = {
      center: this.map.getView().getCenter(),
      zoom: this.map.getView().getZoom(),
      maxZoom: OL_MAX_ZOOM,
      extent: MapUtils.getExtent(),
    };

    // 변경된 지도 VWorld 인 경우
    if (mapId === 'VWORLD') {
      options.extent = MapUtils.getVWorldExtent();
    }

    const view = new View(options);
    view.on('change:center', this.map.getView().listeners_['change:center'][0]);
    this.map.setView(view);
  }

  /**
   * 오버레이 생성
   * @param {Object} options - Overlay 옵션
   * @returns {Overlay} 생성된 오버레이
   */
  createOverlay(options) {
    options.element.addEventListener('wheel', (e) => {
      this.map.getViewport().dispatchEvent(new WheelEvent(e.type, e));
    });

    return new Overlay(options);
  }

  /**
   * 맵 인스턴스 가져오기
   * @returns {Map} OpenLayers 맵 인스턴스
   */
  getMap() {
    return this.map;
  }

  /**
   * 현재 맵 ID 가져오기
   * @returns {string} 현재 맵 ID
   */
  getCurrentMapId() {
    return this.currentMapId;
  }
}

export default MapCore;
